/**
 * Jubly Calendar Event Detail Component
 * Full event view with attendee management and actions
 */

import { useState } from 'react';
import {
  X,
  Edit2,
  Trash2,
  Clock,
  MapPin,
  AlignLeft,
  Users,
  Repeat,
  Mail,
  Check,
  XCircle,
  HelpCircle,
  ArrowLeft,
  Share2,
  Calendar,
  Video
} from 'lucide-react';

const STATUS_CONFIG = {
  accepted: { label: 'Going', color: 'text-green-600 bg-green-50', icon: Check },
  declined: { label: 'Declined', color: 'text-red-600 bg-red-50', icon: XCircle },
  tentative: { label: 'Maybe', color: 'text-yellow-600 bg-yellow-50', icon: HelpCircle },
  pending: { label: 'Pending', color: 'text-gray-600 bg-gray-50', icon: HelpCircle }
};

export default function EventDetail({
  event,
  isOpen,
  onClose,
  onEdit,
  onDelete,
  onAddAttendees,
  onRemoveAttendee,
  currentUser
}) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  if (!event || !isOpen) return null;

  const isOrganizer = event.user_id === currentUser?.id;
  const currentAttendee = event.attendees?.find(a => a.email === currentUser?.email);

  const formatDate = (dateStr, isAllDay) => {
    const date = new Date(dateStr);
    if (isAllDay) {
      return date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        month: 'long', 
        day: 'numeric',
        year: 'numeric'
      });
    }
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'long', 
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const formatTime = (dateStr) => {
    return new Date(dateStr).toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const getRecurrenceLabel = (rule) => {
    if (!rule) return null;
    if (rule.includes('DAILY')) return 'Repeats daily';
    if (rule.includes('WEEKLY') && rule.includes('MO,WE,FR')) return 'Repeats Mon, Wed, Fri';
    if (rule.includes('WEEKLY') && rule.includes('MO,TU,WE,TH,FR')) return 'Repeats every weekday';
    if (rule.includes('WEEKLY')) return 'Repeats weekly';
    if (rule.includes('MONTHLY')) return 'Repeats monthly';
    if (rule.includes('YEARLY')) return 'Repeats yearly';
    return 'Repeats';
  };

  const handleDelete = async () => {
    if (!showDeleteConfirm) {
      setShowDeleteConfirm(true);
      return;
    }
    
    setIsDeleting(true);
    try {
      await onDelete?.(event.id);
      onClose();
    } catch (err) {
      console.error('Failed to delete event:', err);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const getAttendeeStatusIcon = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    const Icon = config.icon;
    return <Icon className={`w-4 h-4 ${config.color.split(' ')[0]}`} />;
  };

  const getAttendeeStatusClass = (status) => {
    return STATUS_CONFIG[status]?.color || STATUS_CONFIG.pending.color;
  };

  const getAttendeeStatusLabel = (status) => {
    return STATUS_CONFIG[status]?.label || 'Pending';
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="p-2 -ml-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h2 className="text-lg font-semibold text-gray-900">Event details</h2>
          </div>
          <div className="flex items-center gap-2">
            {isOrganizer && (
              <>
                <button
                  onClick={() => onEdit?.(event)}
                  className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Edit2 className="w-5 h-5" />
                </button>
                <button
                  onClick={handleDelete}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Title & Calendar */}
          <div>
            <div 
              className="w-4 h-4 rounded-full mb-3"
              style={{ backgroundColor: event.calendar_color || '#3B82F6' }}
            />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {event.title}
            </h1>
            <p className="text-sm text-gray-500">
              {event.calendar_name || 'My Calendar'}
            </p>
          </div>

          {/* Date/Time */}
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
              <Clock className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <div className="font-medium text-gray-900">
                {event.is_all_day ? (
                  formatDate(event.start_time, true)
                ) : (
                  <>
                    {formatDate(event.start_time, false)}
                    <br />
                    <span className="text-gray-500">
                      {formatTime(event.start_time)} - {formatTime(event.end_time)}
                    </span>
                  </>
                )}
              </div>
              {event.recurrence_rule && (
                <div className="flex items-center gap-2 mt-2 text-sm text-gray-600">
                  <Repeat className="w-4 h-4" />
                  {getRecurrenceLabel(event.recurrence_rule)}
                </div>
              )}
            </div>
          </div>

          {/* Meeting Link */}
          {event.location && (event.location.includes('jubly.net/meeting') || event.location.includes('/meeting/')) && (
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                <Video className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <a
                  href={event.location}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Video className="w-4 h-4" />
                  Join Meeting
                </a>
                <p className="text-xs text-gray-400 mt-1.5 break-all">{event.location}</p>
              </div>
            </div>
          )}

          {/* Location (non-meeting) */}
          {event.location && !event.location.includes('jubly.net/meeting') && !event.location.includes('/meeting/') && (
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <div className="font-medium text-gray-900">{event.location}</div>
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent(event.location)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline"
                >
                  View on map
                </a>
              </div>
            </div>
          )}

          {/* Description */}
          {event.description && (
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0">
                <AlignLeft className="w-5 h-5 text-gray-600" />
              </div>
              <div className="flex-1">
                <div className="text-sm text-gray-700 whitespace-pre-wrap">
                  {event.description}
                </div>
              </div>
            </div>
          )}

          {/* Attendees */}
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
              <Users className="w-5 h-5 text-purple-600" />
            </div>
            <div className="flex-1">
              <div className="font-medium text-gray-900 mb-3">
                {event.attendees?.length || 0} {event.attendees?.length === 1 ? 'attendee' : 'attendees'}
              </div>
              
              <div className="space-y-2">
                {event.attendees?.map(attendee => (
                  <div 
                    key={attendee.email}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white text-sm font-medium">
                        {(attendee.name || attendee.email).charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-medium text-sm">{attendee.name || attendee.email}</div>
                        <div className="text-xs text-gray-500">{attendee.email}</div>
                      </div>
                    </div>
                    <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${getAttendeeStatusClass(attendee.status)}`}>
                      {getAttendeeStatusIcon(attendee.status)}
                      {getAttendeeStatusLabel(attendee.status)}
                    </div>
                  </div>
                ))}
              </div>

              {isOrganizer && (
                <button
                  onClick={() => onAddAttendees?.(event.id)}
                  className="mt-3 flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  <Mail className="w-4 h-4" />
                  Invite more people
                </button>
              )}
            </div>
          </div>

          {/* Organizer info */}
          {event.user_id && (
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0">
                <Calendar className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <div className="font-medium text-gray-900">Organizer</div>
                <div className="text-sm text-gray-500">
                  {isOrganizer ? 'You are the organizer' : 'Organized by someone else'}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer actions for non-organizers */}
        {!isOrganizer && currentAttendee && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="text-sm font-medium text-gray-700 mb-3">Your response:</div>
            <div className="flex gap-2">
              <button
                onClick={() => {/* Update RSVP */}}
                className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                  currentAttendee.status === 'accepted' 
                    ? 'bg-green-600 text-white' 
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Going
              </button>
              <button
                onClick={() => {/* Update RSVP */}}
                className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                  currentAttendee.status === 'tentative' 
                    ? 'bg-yellow-500 text-white' 
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Maybe
              </button>
              <button
                onClick={() => {/* Update RSVP */}}
                className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                  currentAttendee.status === 'declined' 
                    ? 'bg-red-500 text-white' 
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Can't go
              </button>
            </div>
          </div>
        )}

        {/* Delete confirmation */}
        {showDeleteConfirm && (
          <div className="px-6 py-4 border-t border-red-200 bg-red-50">
            <p className="text-sm text-red-700 mb-3">
              Are you sure? This will delete the event for all attendees.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {isDeleting ? 'Deleting...' : 'Delete event'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
